import type { ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, ViewChild, inject } from '@angular/core';

import { type Observable, Subject, distinctUntilChanged, filter, map, of, startWith, takeUntil } from 'rxjs';

import { IconLibrary } from '@finnairoyj/fcom-ui-styles';

import type { DdsLanguage } from '@fcom/core';
import type { CmsArticle, CmsImageData, CmsPicture, CmsVideo } from '@fcom/core-api';
import { CmsContentType } from '@fcom/core-api';
import { symbolToLangPath } from '@fcom/core/utils/cms-path-helpers';
import { finShare } from '@fcom/rx';
import type { IconName } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { DEFAULT_CMS_HEADING_TAG_LEVEL } from '@fcom/ui-components';

import { getHeaderLevel } from '../../services/cms-utils/cms-utils';

@Component({
  selector: 'fin-rich-text',
  styleUrls: ['./rich-text.component.scss'],
  templateUrl: './rich-text.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RichTextComponent implements OnDestroy, OnInit {
  private languageService = inject(LanguageService);
  private ngDestroyed$: Subject<boolean> = new Subject();

  readonly DEFAULT_HEADER_LEVEL = DEFAULT_CMS_HEADING_TAG_LEVEL;
  readonly IconLibrary = IconLibrary;

  @Input() headerLevel: number;
  @Input() headerStyles = [];
  @Input() hideMediaContentImage = false;
  @Input() scaleImage = true;

  @Input() content$: Observable<CmsArticle>;
  @Input() icons$: Observable<Array<{ name: IconName; category?: IconLibrary }>>;
  @Input() media$: Observable<CmsPicture[] | CmsVideo[]>;
  @Input() styleClasses$: Observable<string[]>;
  @Input() title$: Observable<string>;

  @ViewChild('articleWrapper', { static: true })
  articleWrapper: ElementRef;

  @ViewChild('contentWrapper', { static: true })
  contentWrapper: ElementRef;

  headerExtraClasses$: Observable<string>;
  headerLevel$: Observable<number>;
  mappedContent$: Observable<string>;
  mappedImage$: Observable<CmsImageData>;
  mappedSegment: string;

  ngOnInit(): void {
    const ddsLocale: DdsLanguage = this.languageService.ddsLocaleValue as unknown as DdsLanguage;

    this.headerLevel$ = this.headerLevel
      ? of(this.headerLevel)
      : this.content$.pipe(
          map((c: CmsArticle): number => c.headerLevel ?? getHeaderLevel(c?.subjectTaxonomyTags, c?.contentId) ?? 0),
          distinctUntilChanged(),
          finShare()
        );

    this.headerExtraClasses$ = this.content$.pipe(
      map((content: CmsArticle): string => this.headerStyles.concat(content?.subjectTaxonomyTags ?? []).join(' ')),
      startWith(this.headerStyles.join(' ')),
      distinctUntilChanged(),
      finShare()
    );

    this.mappedContent$ = this.content$.pipe(
      map((content: CmsArticle): string => content.detailText),
      map((contentHtml: string): string => {
        const parsedContent: string = contentHtml
          .replace(/<table\b/g, '<div class="rich-text-table-wrapper pr-xsmall-t pr-medium-b"><table')
          .replace(/<\/table>/g, '</table></div>');
        return symbolToLangPath(parsedContent, ddsLocale);
      })
    );

    this.mappedImage$ = this.media$.pipe(
      filter((media: CmsPicture[] | CmsVideo[]): boolean => media.length > 0),
      filter((media: CmsPicture[] | CmsVideo[]): boolean => media[0].contentType === CmsContentType.CMPicture),
      map((media: CmsPicture[]): CmsPicture => media[0]),
      distinctUntilChanged(),
      finShare()
    );

    this.content$.pipe(takeUntil(this.ngDestroyed$)).subscribe((content: CmsArticle): void => {
      this.mappedSegment = content.segment;
    });
  }

  ngOnDestroy(): void {
    this.ngDestroyed$.next(true);
  }
}
